'use client';

import cs from 'classnames';
import { styled } from 'styled-components';
import * as Styled from '@lib/styles';
import FullColumnText from './FullColumnText';
import Button from '../components/Button';
import { CtaProps } from '@types';
import BaseText from '@dvd/components/BaseText';
import HubSpotForm from '@dvd/components/HubSpotForm';

/**
 * Note: When updating props please also update the story descriptions.
 * Because we're using unions we have to place the descriptions in the story file.
 */

// Define the base props that all variants share
type BaseCtaModuleProps = {
  /**
   * Required! headline for the cta
   */
  headline: string;
  /**
   * Optional description for cta
   */
  description?: string;
  /**
   * Optional eyebrow text for the cta
   */
  eyebrowText?: string;
  /**
   * Color Variant:
   * - light
   * - dark (default)
   */
  colorVariant?: 'dark' | 'light';
};

// Define props for button-based CTAs (fullBody and pill variants)
type ButtonCtaProps = BaseCtaModuleProps & {
  variant?: 'fullBody' | 'pill';
  /**
   * Required! Primary Cta
   */
  primaryCta: CtaProps;
  /**
   * Optional secondary cta href
   */
  secondaryCta?: CtaProps;
  formSettings?: never; // Explicitly disallow formSettings
};

// Define props for form-based CTAs
type FormCtaProps = BaseCtaModuleProps & {
  /** Variant: form */
  variant: 'form';
  /** Form configuration (required when variant is "form"). The configuration object should include the formId and an optionalformTitle. */
  formSettings: {
    /** The formId is the unique identifier for the form in HubSpot. */
    formId: string;
    /** The formTitle is the title of the form. */
    formTitle?: string;
  };
  primaryCta?: never; // Explicitly disallow primaryCta
  secondaryCta?: never; // Explicitly disallow secondaryCta
};

// Union type that enforces mutual exclusivity
type CtaModuleProps = ButtonCtaProps | FormCtaProps;

const CtaContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.blue700};
  border-radius: ${({ theme }) => theme.radius.std};
  color: ${({ theme }) => theme.colors.white};
  margin-block: ${({ theme }) => theme.spacing[3]};

  h2,
  h3 {
    text-wrap: initial;
  }

  &.light {
    background-color: ${({ theme }) => theme.colors.blue100};
    color: ${({ theme }) => theme.colors.blue700};
  }

  &.fullBody {
    padding-block: ${({ theme }) => theme.spacing[2.5]};
    padding-inline: ${({ theme }) => theme.spacing[2]};
  }

  &.pill {
    align-items: center;
    display: flex;
    flex-direction: column;
    margin-block: ${({ theme }) => theme.spacing[2.5]};
    padding: 2.25rem ${({ theme }) => theme.spacing[2]};
    text-align: center;
  }

  &.form {
    align-items: center;
    display: flex;
    flex-direction: column;
    text-align: center;
    padding-block: ${({ theme }) => theme.spacing[4]};
    padding-inline: ${({ theme }) => theme.spacing[2]};

    .Cta__form {
      width: 100%;
    }

    .Cta__form-text {
      text-align: start;
    }

    // Override the default color for the form title to match the cta color variant
    &.light {
      h3 {
        color: ${({ theme }) => theme.colors.blue700};
      }
    }

    // Override the default color for the form title to match the cta color variant
    &.dark {
      h3 {
        color: ${({ theme }) => theme.colors.white};
      }
    }
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    &.form {
      flex-direction: column;
      text-align: start;

      .Cta__form-text {
        text-align: start;
      }
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    gap: ${({ theme }) => theme.spacing[1.5]};
    margin-block: ${({ theme }) => theme.spacing[4]};

    &.fullBody {
      padding-block: ${({ theme }) => theme.spacing[5]};
      padding-inline: ${({ theme }) => theme.spacing[4]};
    }

    &.pill {
      border-radius: ${({ theme }) => theme.radius.btn};
      flex-direction: row;
      justify-content: center;
      padding-block: ${({ theme }) => theme.spacing[1]};
      padding-inline: ${({ theme }) => theme.spacing[4]};

      h3 {
        margin-bottom: 0;
      }
    }

    &.form {
      padding-block: ${({ theme }) => theme.spacing[5]};
      padding-inline: ${({ theme }) => theme.spacing[4]};
      display: grid;
      grid-template-columns: 1fr 2fr;
      gap: ${({ theme }) => theme.spacing[2]};
      text-align: start;

      .Cta__form-text {
        text-align: start;
        padding-inline: unset;
      }
    }
  }
`;

const CtaFullColumnText = styled(FullColumnText)`
  padding-top: 0;
`;

const CtaButtonRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};

  ${({ theme }) => theme.mq.minWidth.large} {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing[1.5]};
    justify-content: center;
  }
`;

/**
 * CTA (Call-to-Action) Component
 *
 * A versatile call-to-action component that supports multiple variants including
 * button-based CTAs (fullBody, pill) and form-based CTAs.
 *
 * **Note:** When using the form variant the form colors will be dependent on what is
 * set in HubSpot. Therefore if we require the form to sit on a dark background
 * you will need to go into HubSpot and set the form colors as intended.
 *
 * While there is a formColorScheme this only applies to forms created with the
 * HubSpot legacy form builder. Since most of our forms are now created using the
 * new form builder the color scheme will not be applied.
 */
const Cta = ({
  description,
  eyebrowText,
  headline,
  primaryCta,
  secondaryCta = null,
  colorVariant = 'dark',
  variant = 'fullBody',
  formSettings = null,
}: CtaModuleProps) => {
  // Type guard to check if this is a form variant
  const isFormVariant = variant === 'form';

  return (
    <CtaContainer className={cs(variant, colorVariant)}>
      {isFormVariant && formSettings && (
        <>
          <BaseText
            headingLevel="h2"
            eyebrow={eyebrowText}
            heading={headline}
            body={description}
            className="Cta__form-text"
          />
          <HubSpotForm
            formId={formSettings.formId}
            title={formSettings.formTitle}
            className="Cta__form"
            insetVariant={colorVariant === 'dark' ? 'dark' : 'light'}
          />
        </>
      )}
      {!isFormVariant && primaryCta && (
        <>
          {variant === 'fullBody' && (
            <>
              <CtaFullColumnText bottomPadding={false}>
                {eyebrowText ? (
                  <Styled.Eyebrow>{eyebrowText}</Styled.Eyebrow>
                ) : null}
                <h2>{headline}</h2>
                {description ? <p>{description}</p> : null}
              </CtaFullColumnText>
              <CtaButtonRow>
                <Button
                  color={primaryCta.buttonColor ?? 'blue500'}
                  href={primaryCta.href}
                  trackingLabel={primaryCta.trackingLabel || primaryCta.text}
                  trackingProperty={
                    primaryCta.trackingProperty || primaryCta.href
                  }
                >
                  {primaryCta.text}
                </Button>
                {secondaryCta ? (
                  <Button
                    color={colorVariant === 'light' ? 'blue500' : 'white'}
                    href={secondaryCta.href}
                    trackingLabel={
                      secondaryCta.trackingLabel || secondaryCta.text
                    }
                    trackingProperty={
                      secondaryCta.trackingProperty || secondaryCta.href
                    }
                    variant="secondary"
                  >
                    {secondaryCta.text}
                  </Button>
                ) : null}
              </CtaButtonRow>
            </>
          )}
          {variant === 'pill' && (
            <>
              <h3>{headline}</h3>
              <Button
                color={primaryCta.buttonColor ?? 'blue500'}
                href={primaryCta.href}
                trackingLabel={primaryCta.trackingLabel || primaryCta.text}
                trackingProperty={
                  primaryCta.trackingProperty || primaryCta.href
                }
              >
                {primaryCta.text}
              </Button>
            </>
          )}
        </>
      )}
    </CtaContainer>
  );
};

export default Cta;
